import { useEffect, useState } from 'react';
import { useT } from '../i18n/useT';
import {
  connectGithub,
  getFolderSyncState,
  getGithubConfig,
  runFolderSync,
  stopFolderSync,
  type FolderSyncState,
} from '../lib/folderSync';
import { askConfirm } from '../lib/dialogs';
import { syncStatusLine } from './syncStatus';

/**
 * Syncing the library with a GitHub repository.
 *
 * This is the same two-way sync as the folder one — the same rules, the same
 * conflict handling — pointed at a repo instead of a disk. It matters because
 * it needs no File System Access API and no markbird account: it works in
 * every browser, on a phone, and the files stay yours in a place you already
 * have, with a history you already trust.
 *
 * The token is typed here and kept on this device. It is not a document, so it
 * is never synced, exported or backed up; another device gets its own.
 */
export function GithubSyncSettings() {
  const t = useT();
  const [state, setState] = useState<FolderSyncState | null>(null);
  const [repo, setRepo] = useState('');
  const [branch, setBranch] = useState('main');
  const [path, setPath] = useState('');
  const [token, setToken] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void getFolderSyncState().then(setState);
    void getGithubConfig().then((config) => {
      if (!config) return;
      setRepo(config.repo);
      setBranch(config.branch);
      setPath(config.path ?? '');
    });
  }, []);

  const connected = state?.enabled === true && state.target === 'github';
  const valid = /^[\w.-]+\/[\w.-]+$/.test(repo.trim()) && branch.trim() !== '' && token !== '';

  const run = async (work: () => Promise<FolderSyncState | null>) => {
    setBusy(true);
    try {
      const next = await work();
      setState(next ?? (await getFolderSyncState()));
    } finally {
      setBusy(false);
    }
  };

  const connect = () =>
    run(async () => {
      setProblem(null);
      const ok = await askConfirm({
        title: t('github.confirmTitle'),
        body: t('github.confirmBody', { repo: repo.trim() }),
        confirmLabel: t('github.connect'),
      });
      if (!ok) return null;
      const result = await connectGithub({
        repo: repo.trim(),
        branch: branch.trim(),
        token,
        path: path.trim(),
      });
      if (!result.ok) {
        setProblem(t(`github.reason.${result.reason}` as 'github.reason.error'));
        return null;
      }
      // The token has done its job; it lives in storage now, not in the form.
      setToken('');
      return runFolderSync(true);
    });

  return (
    <div className="mb-setting mb-setting-block">
      <label>{t('github.title')}</label>
      <div className="mb-backup">
        <p className="mb-modal-note">{t('github.hint')}</p>

        {connected ? (
          <div className="mb-backup-row">
            <span className="mb-backup-folder">
              {t('github.connectedTo', { name: state?.folderName ?? '' })}
            </span>
            <button
              className="mb-btn"
              disabled={busy}
              onClick={() => void run(() => runFolderSync(true))}
            >
              {t('folderSync.now')}
            </button>
            <button className="mb-btn" disabled={busy} onClick={() => void run(stopFolderSync)}>
              {t('github.disconnect')}
            </button>
          </div>
        ) : (
          <>
            <div className="mb-backup-row">
              <label className="mb-field">
                <span>{t('github.repo')}</span>
                <input
                  value={repo}
                  onChange={(e) => setRepo(e.target.value)}
                  placeholder="owner/notes"
                  autoComplete="off"
                  spellCheck={false}
                />
              </label>
              <label className="mb-field">
                <span>{t('github.branch')}</span>
                <input
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
              </label>
            </div>
            <div className="mb-backup-row">
              <label className="mb-field">
                <span>{t('github.path')}</span>
                <input
                  value={path}
                  onChange={(e) => setPath(e.target.value)}
                  placeholder="notes"
                  autoComplete="off"
                  spellCheck={false}
                />
              </label>
              <label className="mb-field">
                <span>{t('github.token')}</span>
                <input
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
              </label>
            </div>
            <p className="mb-modal-note">{t('github.tokenHint')}</p>
            <div className="mb-backup-row">
              <button className="mb-btn" disabled={busy || !valid} onClick={() => void connect()}>
                {t('github.connect')}
              </button>
            </div>
          </>
        )}

        <p className="mb-backup-msg" role="status">
          {problem ?? syncStatusLine(connected ? state : null, t, 'github.notConnected')}
        </p>
      </div>
    </div>
  );
}
